import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';

const SolicitudesCambioPlacaModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useContext(AuthContext);
  const isSuperadmin = user?.role === 'superadmin' || user?.profile_name === 'superadmin';

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pendiente'); // 'pendiente', 'aprobado', 'rechazado'
  const [processingId, setProcessingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState({});
  const [showRejectInput, setShowRejectInput] = useState({});

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/plate-change/requests?status=${activeTab}`);
      setRequests(res.data);
    } catch (err) {
      console.error('Error al cargar solicitudes de cambio de placa:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (isOpen) {
      fetchRequests();
    }
  }, [isOpen, fetchRequests]);

  const handleApprove = async (req) => {
    const isTransitChange = req.request_type === 'transit_change';
    const confirmMsg = isTransitChange
      ? `¿Confirmas aprobar la desvinculación y eliminación de placa del Trámite #${req.tramite_id} en este Organismo de Tránsito?`
      : `¿Confirmas aprobar el cambio de placa para el Trámite #${req.tramite_id} a la placa reservada ${req.requested_plate_number || ''}?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      setProcessingId(req.id);
      const res = await axios.post(`/api/plate-change/requests/${req.id}/approve`);
      alert(`✅ ${res.data.message}`);
      fetchRequests();
      if (onSuccess) onSuccess();
    } catch (err) {
      alert(`❌ Error: ${err.response?.data?.message || err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (reqId, tramiteId) => {
    const reason = rejectionReason[reqId];
    if (!reason || !reason.trim()) {
      alert('⚠️ Debe ingresar un motivo de rechazo');
      return;
    }

    try {
      setProcessingId(reqId);
      const res = await axios.post(`/api/plate-change/requests/${reqId}/reject`, {
        rejection_reason: reason
      });
      alert(`ℹ️ ${res.data.message}`);
      fetchRequests();
      if (onSuccess) onSuccess();
    } catch (err) {
      alert(`❌ Error: ${err.response?.data?.message || err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '900px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h3>📩 Solicitudes de Cambio de Placa (Aprobación Tránsito)</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div style={{ display: 'flex', gap: '10px', padding: '15px 0', borderBottom: '1px solid var(--card-border)' }}>
          <button
            onClick={() => setActiveTab('pendiente')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: '700',
              cursor: 'pointer',
              background: activeTab === 'pendiente' ? 'var(--primary-color)' : 'var(--secondary-soft)',
              color: activeTab === 'pendiente' ? 'white' : 'var(--text-secondary)'
            }}
          >
            ⏳ Pendientes ({activeTab === 'pendiente' ? requests.length : '...'})
          </button>
          <button
            onClick={() => setActiveTab('aprobado')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: '700',
              cursor: 'pointer',
              background: activeTab === 'aprobado' ? '#10b981' : 'var(--secondary-soft)',
              color: activeTab === 'aprobado' ? 'white' : 'var(--text-secondary)'
            }}
          >
            ✅ Aprobados
          </button>
          <button
            onClick={() => setActiveTab('rechazado')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: '700',
              cursor: 'pointer',
              background: activeTab === 'rechazado' ? '#ef4444' : 'var(--secondary-soft)',
              color: activeTab === 'rechazado' ? 'white' : 'var(--text-secondary)'
            }}
          >
            ❌ Rechazados
          </button>
        </div>

        <div style={{ padding: '20px 0' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>⏳ Cargando solicitudes...</p>
            </div>
          ) : requests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              <p style={{ fontSize: '15px', fontWeight: '600' }}>No hay solicitudes de cambio de placa en estado <strong>{activeTab}</strong>.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {requests.map((req) => (
                <div
                  key={req.id}
                  style={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    padding: '18px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--primary-color)' }}>
                        Trámite #{req.tramite_id}
                      </span>
                      {req.request_type === 'transit_change' && (
                        <span style={{ marginLeft: '10px', fontSize: '11px', background: '#dbeafe', color: '#1e40af', padding: '3px 8px', borderRadius: '6px', fontWeight: '800' }}>
                          🚚 TRASLADO DE TRÁNSITO / ELIMINACIÓN
                        </span>
                      )}
                      <span style={{ marginLeft: '12px', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                        🏢 {req.concesionario_nombre || 'Concesionario'}
                      </span>
                      <span style={{ marginLeft: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        👤 Solicita: @{req.requested_by_username || 'usuario'} ({new Date(req.created_at).toLocaleString('es-CO')})
                      </span>
                    </div>

                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '800',
                      background: req.status === 'pendiente' ? '#fef3c7' : req.status === 'aprobado' ? '#d1fae5' : '#fee2e2',
                      color: req.status === 'pendiente' ? '#d97706' : req.status === 'aprobado' ? '#059669' : '#dc2626'
                    }}>
                      {req.status.toUpperCase()}
                    </span>
                  </div>

                  {req.request_type === 'transit_change' && (
                    <div style={{
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      fontSize: '13px',
                      color: '#1e3a8a',
                      fontWeight: '600'
                    }}>
                      {isSuperadmin ? (
                        <>🏛️ <strong>Solicitud de Traslado:</strong> El concesionario solicita eliminar/desvincular la placa de <strong>{req.transito_nombre || 'Tránsito Actual'}</strong> para radicar el trámite en <strong>{req.target_transito_nombre || 'Nuevo Tránsito'}</strong>.</>
                      ) : (
                        <>🏛️ <strong>Solicitud de Eliminación por Traslado:</strong> El concesionario solicita desvincular y liberar la placa asignada en este Organismo de Tránsito debido a una solicitud de cambio de tránsito.</>
                      )}
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    background: 'var(--secondary-soft)',
                    padding: '14px',
                    borderRadius: '10px',
                    marginBottom: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '800', color: '#64748b' }}>
                        {req.request_type === 'transit_change' ? 'PLACA EN TRÁNSITO ACTUAL' : 'PLACA ACTUAL'}
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>
                        {req.current_plate_number || req.datos_adicionales?.placa_referencia || 'SIN PLACA'}
                      </div>
                    </div>

                    <div style={{ fontSize: '20px', color: 'var(--primary-color)' }}>➔</div>

                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '800', color: '#2563eb' }}>
                        {req.request_type === 'transit_change' 
                          ? (isSuperadmin ? `NUEVA PLACA EN ${req.target_transito_nombre || 'NUEVO TRÁNSITO'}` : 'ACCIÓN SOLICITADA')
                          : 'NUEVA PLACA RESERVADA'}
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: '900', color: '#2563eb', background: '#dbeafe', padding: '2px 10px', borderRadius: '6px', display: 'inline-block' }}>
                        {req.request_type === 'transit_change'
                          ? (req.requested_plate_number 
                              ? `${req.requested_plate_number} (${req.requested_vehicle_type || 'Vehículo'})` 
                              : (isSuperadmin ? 'ASIGNACIÓN EN NUEVO TRÁNSITO' : 'DESVINCULAR Y LIBERAR PLACA'))
                          : (req.requested_plate_number ? `${req.requested_plate_number} (${req.requested_vehicle_type || 'Vehículo'})` : 'PLACA POR ASIGNAR')
                        }
                      </div>
                    </div>
                  </div>

                  {req.reason && (
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                      <strong>Motivo:</strong> {req.reason}
                    </div>
                  )}

                  {req.rejection_reason && (
                    <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '12px', fontWeight: '600' }}>
                      <strong>Motivo de Rechazo:</strong> {req.rejection_reason}
                    </div>
                  )}

                  {req.status === 'pendiente' && (
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        onClick={() => handleApprove(req)}
                        disabled={processingId === req.id}
                        style={{
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '8px 18px',
                          borderRadius: '8px',
                          fontWeight: '800',
                          cursor: 'pointer'
                        }}
                      >
                        {processingId === req.id ? '⏳ Procesando...' : (req.request_type === 'transit_change' ? '✅ Aprobar Eliminación / Traslado' : '✅ Aprobar Cambio')}
                      </button>

                      {!showRejectInput[req.id] ? (
                        <button
                          onClick={() => setShowRejectInput({ ...showRejectInput, [req.id]: true })}
                          disabled={processingId === req.id}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '8px 18px',
                            borderRadius: '8px',
                            fontWeight: '800',
                            cursor: 'pointer'
                          }}
                        >
                          ❌ Rechazar
                        </button>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder="Escribe el motivo del rechazo..."
                            value={rejectionReason[req.id] || ''}
                            onChange={(e) => setRejectionReason({ ...rejectionReason, [req.id]: e.target.value })}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: '1px solid var(--input-border)',
                              fontSize: '13px'
                            }}
                          />
                          <button
                            onClick={() => handleReject(req.id, req.tramite_id)}
                            disabled={processingId === req.id}
                            style={{
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              padding: '8px 14px',
                              borderRadius: '8px',
                              fontWeight: '800',
                              cursor: 'pointer'
                            }}
                          >
                            Confirmar Rechazo
                          </button>
                          <button
                            onClick={() => setShowRejectInput({ ...showRejectInput, [req.id]: false })}
                            style={{
                              background: 'transparent',
                              color: 'var(--text-secondary)',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '700'
                            }}
                          >
                            Cancelar
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SolicitudesCambioPlacaModal;
